import {
  Dispatch,
  forwardRef,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import debounce from 'lodash.debounce'
import { ArrowDropDown, Clear } from '@mui/icons-material'
import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Divider,
  List,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'

type SuggestedSearchesProps = {
  suggestedSearches: [string, string][]
  setInputValue: Dispatch<SetStateAction<string>>
}

function SuggestedSearches({
  suggestedSearches,
  setInputValue,
}: SuggestedSearchesProps) {
  return (
    <Box sx={{ pt: 1 }}>
      {suggestedSearches.map(([key, suggestion]) => (
        <MenuItem
          key={key}
          onClick={() => {
            setInputValue(suggestion || '')
          }}
          sx={{ py: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary">
            Suggested Search: {suggestion}
          </Typography>
        </MenuItem>
      ))}

      {suggestedSearches.length > 0 && <Divider flexItem sx={{ mb: -1 }} />}
    </Box>
  )
}

type ListBoxProps = {
  children: ReactNode
  hasInputValue: boolean
  popperScrollRef: React.MutableRefObject<number>
  setInputValue: Dispatch<SetStateAction<string>>
  suggestedSearches?: [string, string][]
}

const ListBox = forwardRef(
  (
    {
      children,
      hasInputValue,
      popperScrollRef,
      suggestedSearches,
      setInputValue,
      ...props
    }: ListBoxProps,
    ref: React.Ref<HTMLUListElement>,
  ) => {
    // This is awful, but MUI autocomplete completely re-renders the list every time the data changes,
    // so it automatically scrolls to the top. This resets the scroll position each time. For some reason,
    // is needs the set timeout.
    const listRef = useRef<HTMLUListElement>(null)
    useEffect(() => {
      if (popperScrollRef.current) {
        setTimeout(() => {
          listRef.current?.scrollTo({
            left: 0,
            top: popperScrollRef.current,
            behavior: 'instant',
          })
        }, 0)
      }
    }, [popperScrollRef])

    return (
      <List disablePadding {...props} ref={ref}>
        {!hasInputValue && suggestedSearches && (
          <SuggestedSearches
            suggestedSearches={suggestedSearches}
            setInputValue={setInputValue}
          />
        )}
        <ul {...props} ref={listRef}>
          {children}
        </ul>
      </List>
    )
  },
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseOption = Record<string, any>

type BasePickerFieldProps<T extends BaseOption> = {
  onChange: (newValue: T | null) => void
  value: T | null
  setDisableArrowKeys: Dispatch<SetStateAction<boolean>>
  enabled: boolean
  setEnabled: Dispatch<SetStateAction<boolean>>
  inputValue: string
  setInputValue: Dispatch<SetStateAction<string>>
  options: T[]
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  searchValue: string
  setSearchValue: Dispatch<SetStateAction<string>>
  hasNextPage?: boolean
  fetchNextPage: () => void
  isLoading?: boolean
  isInTable?: boolean
  openOnFocus?: boolean
  suggestedSearches?: [string, string][]
} & Omit<TextFieldProps, 'onChange' | 'value'> &
  Pick<
    AutocompleteProps<T, false, true | false, false, 'div'>,
    | 'isOptionEqualToValue'
    | 'getOptionLabel'
    | 'getOptionKey'
    | 'noOptionsText'
    | 'renderOption'
  >

export default function BasePickerField<T extends BaseOption>({
  value,
  setDisableArrowKeys,
  label,
  enabled,
  setEnabled,
  inputValue,
  setInputValue,
  onChange,
  options,
  open,
  setOpen,
  searchValue,
  setSearchValue,
  hasNextPage,
  fetchNextPage,
  isLoading,
  suggestedSearches,
  openOnFocus = false,
  noOptionsText = 'No options',
  isInTable,
  isOptionEqualToValue,
  getOptionLabel,
  getOptionKey,
  renderOption,
  ...props
}: BasePickerFieldProps<T>) {
  const handleOpen = (e: React.SyntheticEvent<Element, Event>) => {
    if (
      e.type !== 'mousedown' &&
      (e as unknown as KeyboardEvent).key !== 'ArrowDown' &&
      (e as unknown as KeyboardEvent).key !== 'ArrowUp'
    ) {
      setOpen(true)
      if (!enabled) setEnabled(true) // We fetch the first time the dialog opens
      setDisableArrowKeys(true)
    }
  }

  const clearingInInputRef = useRef(false)
  const popperScrollRef = useRef(0)

  const onScroll: React.UIEventHandler<HTMLUListElement> = (event) => {
    const target = event.target as HTMLDivElement
    const bottom =
      Math.abs(target.clientHeight - (target.scrollHeight - target.scrollTop)) <
      5
    if (bottom && hasNextPage) {
      fetchNextPage()
    }
    popperScrollRef.current = target.scrollTop
  }

  const debouncedUpdateValue = useMemo(
    () =>
      debounce((newValue: string) => {
        setSearchValue(newValue)
      }, 200),
    [setSearchValue],
  )

  // Debounce updating the search value
  useEffect(() => {
    if (inputValue !== searchValue) {
      debouncedUpdateValue(inputValue)
    }
  }, [debouncedUpdateValue, inputValue, searchValue])

  return (
    <Autocomplete<T, false, true | false>
      disabled={props.disabled}
      fullWidth
      openOnFocus={openOnFocus}
      clearIcon={<Clear fontSize="inherit" />}
      popupIcon={<ArrowDropDown fontSize="inherit" />}
      loading={isLoading}
      value={value}
      options={options || []}
      disableClearable
      onChange={(_event, newValue, reason) => {
        // If the user clears the input by using the keyboard, we don't want to
        // make a call yet because they might be typing a new value
        if (!newValue && reason === 'clear' && clearingInInputRef.current) {
          clearingInInputRef.current = false
        } else {
          onChange(newValue)
        }
      }}
      open={open}
      onOpen={handleOpen}
      onClose={() => {
        setOpen(false)
        setDisableArrowKeys(false)
        setSearchValue('')
      }}
      inputValue={inputValue}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionKey}
      renderOption={renderOption}
      onInputChange={(_event, newInputValue, reason) => {
        if (!newInputValue && reason === 'input') {
          clearingInInputRef.current = true
        }
        setInputValue(newInputValue)
      }}
      filterOptions={(x) => x}
      renderInput={(params) => {
        return (
          <TextField
            {...props}
            {...params}
            label={label}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                ...props.inputProps,
                style: {
                  ...params.inputProps.style,
                  ...props.inputProps?.style,
                  fontStyle: value?.id === 'None' ? 'italic' : undefined,
                  color: value?.id === 'None' ? 'gray' : undefined,
                },
              },
              input: {
                ...params.InputProps,
                ...props.InputProps,
              },
              inputLabel: {
                ...params.InputLabelProps,
                ...props.InputLabelProps,
              },
            }}
          />
        )
      }}
      noOptionsText={noOptionsText}
      slotProps={{
        clearIndicator: {
          size: isInTable ? 'small' : undefined,
        },
        popupIndicator: {
          size: isInTable ? 'small' : undefined,
        },
        popper: {
          sx: {
            minWidth: 'fit-content',
            width: 'max-content',
            zIndex: (theme) => theme.zIndex.modal + 2,
            gap: 0,
          },
        },
        listbox: {
          onScroll,
          sx: {
            mt: -1,
          },
          component: ListBox,
          // @ts-expect-error -- Not sure how to type this since its so deeply nested
          hasInputValue: !!inputValue,
          popperScrollRef,
          suggestedSearches,
          setInputValue,
        },
      }}
    />
  )
}
